import { HttpClient, HttpResponseError } from 'utils/httpClient'
import { BASE_API_URL } from 'utils/constants'

export interface CppCitation {
  claim_id: string
  source_id: string
  source_title: string | null
  document_type: string
  effective_date: string | null
  locator: string | null
  quote: string | null
  asserted_by?: string | null
  confidence?: number | null
}

export interface CppBriefItem {
  glyph: string
  text: string
  citation: CppCitation
}

export interface CppBriefSection {
  type: string
  title: string
  items: CppBriefItem[]
  truncated_count: number
}

export interface CppBriefPayload {
  patient: { name: string; age: number | null; service_type: string | null }
  cert_period: { number: number; start: string; end: string | null } | null
  visit: { scheduled_at: string | null }
  since: { date: string; basis: string }
  narrative: string | null
  sections: CppBriefSection[]
  snapshot_id?: string | null
}

export interface CppSourceSummary {
  id: string
  document_type: string
  source_system: string
  title: string | null
  effective_date: string | null
  effective_date_basis: string
  page_count: number | null
  abstract: string | null
  covers: string[]
  text_available: boolean
  extraction_status: string
  extraction_report: Record<string, unknown> | null
}

export interface CppEvidenceQuote {
  ref: string
  quote: string
  citation?: CppCitation
}

export interface CppTask {
  ref?: string
  category?: string
  task: string
  intervention_goal?: string | null
}

export interface CppAdlGoal {
  text: string
  measure: string
  timeframe_days: number
  status: 'new' | 'in_progress' | 'met_and_ongoing'
  prior_goal_ref?: string | null
  prior_goal_citation?: CppCitation | null
  evidence: CppEvidenceQuote[]
  gaps?: string[]
}

export interface CppAdlNeed {
  adl_name: string
  assistance_level: string
  cna_percent: number | null
  narrative: string
  evidence: CppEvidenceQuote[]
  task_ref?: string | null
  task?: CppTask | null
  goal: CppAdlGoal
  confidence: number
  gaps: string[]
}

export interface CppCertPeriodGoal {
  text: string
  rationale?: string
  evidence: CppEvidenceQuote[]
  adl_names: string[]
  gaps?: string[]
}

export interface CppOmittedAdl {
  adl_name: string
  reason: string
}

export interface CppCarePlanDraftOutput {
  cert_period_goal: CppCertPeriodGoal
  adl_needs: CppAdlNeed[]
  omitted_adls: CppOmittedAdl[]
}

export type CppCarePlanDraftStatus = 'queued' | 'generating' | 'ready' | 'failed'

export interface CppCarePlanDraftSummary {
  id: string
  /** Null until generation picks the default period. */
  cert_period_start: string | null
  cert_period_end: string | null
  status: CppCarePlanDraftStatus
  generator_version: string
  created_at: string
  updated_at: string
}

export interface CppCarePlanDraftDetail extends CppCarePlanDraftSummary {
  patient_id: string
  note_source_ids: string[]
  evidence_source_ids: string[]
  generated: CppCarePlanDraftOutput | null
  edited: CppCarePlanDraftOutput | null
  validation_report: Record<string, number> | null
  error: string | null
}

async function readDetail(response: Response): Promise<string> {
  try {
    const body = await response.json()
    if (typeof body?.detail === 'string') return body.detail
  } catch {
    // A non-JSON body carries nothing to show.
  }
  return `Care plan prep request failed: ${response.status}`
}

/**
 * Read the pre-visit brief for a patient.
 *
 * @param patientId - Medplum Patient id
 * @param since - Optional override for the "what changed" window start
 * @returns The rendered brief, with every item carrying a citation
 * @throws HttpResponseError - Carries the status, so a 404 (flag off) can be told apart from other failures
 */
export async function getPatientBrief(patientId: string, since?: string): Promise<CppBriefPayload> {
  const params = new URLSearchParams()
  if (since) params.append('since', since)
  const queryString = params.toString()
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/brief${
    queryString ? `?${queryString}` : ''
  }`

  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as CppBriefPayload
}

export interface CppClaimRow {
  id: string
  kind: string
  subject: string
  attrs: Record<string, unknown>
  valid_from: string
  valid_to: string | null
  status: string
  source_id: string
  supersedes_id: string | null
}

/**
 * List claims held for a patient, newest first.
 *
 * @param patientId - Medplum Patient id
 * @param status - Filter to one claim status. Defaults to `active`, so a superseded
 * claim does not read as a current fact. Pass `null` for every status.
 * @returns The matching claims
 */
export async function listPatientClaims(
  patientId: string,
  status: string | null = 'active'
): Promise<{ claims: CppClaimRow[] }> {
  const params = status ? `?status=${encodeURIComponent(status)}` : ''
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(
    patientId
  )}/claims${params}`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as { claims: CppClaimRow[] }
}

/**
 * List the source catalog for a patient, newest first.
 *
 * @param patientId - Medplum Patient id
 * @returns Every active source with its abstract and citation metadata
 */
export async function listPatientSources(
  patientId: string
): Promise<{ sources: CppSourceSummary[]; total: number }> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/sources`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as { sources: CppSourceSummary[]; total: number }
}

/**
 * Read one source's detail, including a presigned download link when available.
 *
 * @param sourceId - Medplum Communication or DocumentReference id
 */
export async function getSourceDetail(
  sourceId: string
): Promise<CppSourceSummary & { download_url: string | null }> {
  const url = `${BASE_API_URL}/care-plan-prep/sources/${encodeURIComponent(sourceId)}`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as CppSourceSummary & { download_url: string | null }
}

/**
 * Read one source's page text.
 *
 * @param sourceId - Medplum Communication or DocumentReference id
 * @param page - Optional page numbers to restrict the read to.
 */
export async function getSourceText(
  sourceId: string,
  page?: number[]
): Promise<{ pages: { page: number; text: string }[] }> {
  const params = new URLSearchParams()
  page?.forEach((p) => params.append('page', String(p)))
  const query = params.toString()
  const url = `${BASE_API_URL}/care-plan-prep/sources/${encodeURIComponent(sourceId)}/text${
    query ? `?${query}` : ''
  }`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as { pages: { page: number; text: string }[] }
}

/**
 * Enqueue ingest for one note Communication just created in the Notes UI.
 *
 * @param patientId - Medplum Patient id
 * @param communicationId - The Communication id the frontend just created
 */
export async function ingestPatientNote(patientId: string, communicationId: string): Promise<void> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(
    patientId
  )}/notes/${encodeURIComponent(communicationId)}/ingest`
  // The API decides if this note's patient is in the pilot, and answers 404 when not.
  const response = await new HttpClient().fetch(url, { method: 'POST', expectedStatusCodes: [404] })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
}

export interface PatientMemorySyncResult {
  status: string
  documents: number
  notes: number
}

/**
 * Queue reading every uploaded document and nurse note of a patient into patient memory.
 *
 * @param patientId - Medplum Patient id
 * @returns The number of documents and notes queued
 */
export async function syncPatientMemory(patientId: string): Promise<PatientMemorySyncResult> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/sync`
  const response = await new HttpClient().fetch(url, { method: 'POST' })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as PatientMemorySyncResult
}

/**
 * Retract one claim, restoring whatever it superseded.
 *
 * @param claimId - the memory fact id, `<table>:<fact id>`
 * @param options - Optional reason and the brief/care plan the claim was retracted from
 */
export async function retractClaim(
  claimId: string,
  options?: {
    reasonCode?: string
    reasonNote?: string
    briefSnapshotId?: string
    carePlanDraftId?: string
  }
): Promise<void> {
  const url = `${BASE_API_URL}/care-plan-prep/claims/${encodeURIComponent(claimId)}/retract`
  const response = await new HttpClient().fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      reason_code: options?.reasonCode ?? null,
      reason_note: options?.reasonNote ?? null,
      brief_snapshot_id: options?.briefSnapshotId ?? null,
      care_plan_draft_id: options?.carePlanDraftId ?? null
    })
  })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
}

/**
 * Queue a new care plan draft for one patient.
 *
 * @param patientId - Medplum Patient id
 * @param options - Optional note/evidence selection and cert period override
 * @returns The queued draft's id
 */
export async function createCarePlan(
  patientId: string,
  options?: {
    note_source_ids?: string[]
    evidence_source_ids?: string[]
    cert_period_start?: string
    cert_period_end?: string
  }
): Promise<{ draft_id: string }> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/care-plans`
  const response = await new HttpClient().fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options ?? {})
  })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as { draft_id: string }
}

/**
 * List care plan drafts for a patient, newest first.
 *
 * @param patientId - Medplum Patient id
 */
export async function listCarePlans(
  patientId: string
): Promise<{ drafts: CppCarePlanDraftSummary[] }> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/care-plans`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as { drafts: CppCarePlanDraftSummary[] }
}

/**
 * Read one care plan draft's full contents.
 *
 * @param draftId - the care plan draft id
 */
export async function getCarePlan(draftId: string): Promise<CppCarePlanDraftDetail> {
  const url = `${BASE_API_URL}/care-plan-prep/care-plans/${encodeURIComponent(draftId)}`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as CppCarePlanDraftDetail
}

/**
 * Save the nurse's edited version of a care plan draft.
 *
 * @param draftId - the care plan draft id
 * @param edited - The edited draft, validated server-side against CarePlanDraftOutput
 */
export async function updateCarePlan(
  draftId: string,
  edited: CppCarePlanDraftOutput
): Promise<CppCarePlanDraftDetail> {
  const url = `${BASE_API_URL}/care-plan-prep/care-plans/${encodeURIComponent(draftId)}`
  const response = await new HttpClient().fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ edited })
  })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as CppCarePlanDraftDetail
}

/**
 * Render one care plan draft as plain 485-style text.
 *
 * @param draftId - the care plan draft id
 */
export async function exportCarePlan(draftId: string): Promise<string> {
  const url = `${BASE_API_URL}/care-plan-prep/care-plans/${encodeURIComponent(draftId)}/export`
  const response = await new HttpClient().fetch(url)
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return await response.text()
}

export type CppChatTool = 'previsit_prep' | 'soap_note' | 'care_plan'

export interface CppChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface CppChatTurn {
  tool: CppChatTool | null
  reply: string
}

/**
 * Pick the patient view that answers the latest chat message.
 *
 * @param patientId - Medplum Patient id
 * @param messages - Conversation so far, ending with the user's message
 * @returns The view to show, if any, and a short reply
 * @throws HttpResponseError - Carries the status, so a 404 (flag off) can be told apart from other failures
 */
export async function postPatientChat(
  patientId: string,
  messages: CppChatMessage[]
): Promise<CppChatTurn> {
  const url = `${BASE_API_URL}/care-plan-prep/patients/${encodeURIComponent(patientId)}/chat`
  const response = await new HttpClient().fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages })
  })
  if (!response.ok) {
    throw new HttpResponseError(response.status, await readDetail(response))
  }
  return (await response.json()) as CppChatTurn
}
